import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  TipData, 
  Group, 
  User, 
  RowItem, 
  DayTotals, 
  RATE_OPTIONS,
  TipDistributionConfig,
  TipDistributionGroup,
  TipDistributionUser,
  TipDistributionDay,
} from '../../types';
import { SelectableShift } from '../../types';
import TipTableHeader from '../TipTableHeader/TipTableHeader';
import TipTableRow from '../TipTableRow/TipTableRow';
import TipTotalsRow from '../TipTotalsRow/TipTotalsRow';
import AddGroupModal from '../AddGroupModal/AddGroupModal';
import AddUserModal from '../AddUserModal/AddUserModal';
import { useTipCalculations } from '../../hooks/useTipCalculations';
import { createTipRecord } from '../../../../../../crud/payments_api/tip_management';
import { ChevronLeft, ChevronRight, Save } from 'react-bootstrap-icons';
import './TipDistributionTable.scss';

interface TipDistributionTableProps {
  tipData: TipData;
  selectedShifts: SelectableShift[];
  onConfigChange?: (config: TipDistributionConfig) => void; // Callback when config changes
  onRecordSaved?: () => void; // Callback when record is successfully saved
  isViewMode?: boolean; // If true, hide edit buttons and save button (read-only view)
  onSaveRecord?: () => Promise<void>; // Callback to save the record
  isSaving?: boolean; // Loading state for save operation
}

const TipDistributionTable: React.FC<TipDistributionTableProps> = ({
  tipData,
  selectedShifts,
  onConfigChange,
  onRecordSaved,
  isViewMode = false,
  onSaveRecord,
  isSaving: isSavingProp,
}) => {
  // Convert TipData to groups/users format
  const initializeGroupsAndUsers = useCallback((): Group[] => {
    const dates = Object.keys(tipData.days);
    if (dates.length === 0) return [];

    // Get all unique users across all days
    const usersMap = new Map<number, { name: string; rate: number; group?: string }>();
    
    dates.forEach(date => {
      const dayUsers = tipData.days[date]?.users || [];
      dayUsers.forEach(user => {
        if (!usersMap.has(user.user_id)) {
          usersMap.set(user.user_id, {
            name: user.user_name,
            rate: user.rate || 100, // Default to 100% if rate is 0 or undefined
            group: user.group,
          });
        }
      });
    });

    // Separate users by group
    const groupsMap = new Map<string, User[]>();
    const ungroupedUsers: User[] = [];

    usersMap.forEach((userData, userId) => {
      const user: User = {
        id: String(userId),
        name: userData.name,
        rate: userData.rate,
        attendance: dates.reduce((acc, date) => {
          const dayData = tipData.days[date];
          const userInDay = dayData?.users.find(u => u.user_id === userId);
          acc[date] = userInDay?.attended?.[date] ?? true;
          return acc;
        }, {} as { [date: string]: boolean }),
      };

      if (userData.group) {
        // User belongs to a group
        const existingUsers = groupsMap.get(userData.group) || [];
        groupsMap.set(userData.group, [...existingUsers, user]);
      } else {
        // User has no group - add as standalone
        ungroupedUsers.push(user);
      }
    });

    // Create Group objects
    const result: Group[] = [];

    // Add groups with users - these are REAL groups
    groupsMap.forEach((users, groupName) => {
      result.push({
        id: `group-${groupName}`,
        name: groupName,
        rate: 100,
        users: users,
        attendance: dates.reduce((acc, date) => ({ ...acc, [date]: true }), {}),
        isRealGroup: true, // This is a real group from API
      });
    });

    // Add ungrouped users as standalone users (NOT real groups)
    ungroupedUsers.forEach(user => {
      result.push({
        id: user.id,
        name: user.name,
        rate: user.rate,
        users: [], // Empty array means this is a standalone user, not a real group
        attendance: user.attendance,
        isRealGroup: false, // This is a standalone user, NOT a group
      });
    });

    return result;
  }, [tipData]);

  const [groups, setGroups] = useState<Group[]>(() => initializeGroupsAndUsers());
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedGroupForUser, setSelectedGroupForUser] = useState<string | null>(null);
  const [newUserName, setNewUserName] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserName, setEditingUserName] = useState('');
  const [groupName, setGroupName] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [isSavingLocal, setIsSavingLocal] = useState(false);
  const isSaving = isSavingProp ?? isSavingLocal;

  const dates = Object.keys(tipData.days).sort();

  // Check if horizontal scrolling is needed
  useEffect(() => {
    const checkScrollNeeded = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    // Check on mount and when dates change
    checkScrollNeeded();

    // Check on window resize
    window.addEventListener('resize', checkScrollNeeded);
    
    // Use ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(checkScrollNeeded);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkScrollNeeded);
      resizeObserver.disconnect();
    };
  }, [dates.length, groups.length]);

  // System default day totals (from API) - stable reference using JSON.stringify comparison
  const systemDayTotals: DayTotals = useMemo(() => {
    const totals: DayTotals = {};
    dates.forEach(date => {
      totals[date] = tipData.days[date]?.value || 0;
    });
    return totals;
  }, [tipData, dates]);

  // User-declared day totals (editable)
  const [userDayTotals, setUserDayTotals] = useState<DayTotals>({});
  
  // Track initialized dates to avoid resetting user edits
  const initializedDatesRef = useRef<string>('');

  // Initialize userDayTotals only when dates array actually changes (new dates)
  useEffect(() => {
    const datesKey = dates.join(',');
    if (initializedDatesRef.current !== datesKey) {
      initializedDatesRef.current = datesKey;
      // Only initialize values for dates that don't already have user values
      setUserDayTotals(prev => {
        const newTotals: DayTotals = {};
        dates.forEach(date => {
          // Keep existing user value if present, otherwise use system default
          newTotals[date] = prev[date] !== undefined ? prev[date] : (tipData.days[date]?.value || 0);
        });
        return newTotals;
      });
    }
  }, [dates, tipData.days]);

  // The dayTotals used for calculations should be the user-declared values
  const dayTotals: DayTotals = useMemo(() => {
    // If userDayTotals is empty, use systemDayTotals
    if (Object.keys(userDayTotals).length === 0) {
      return systemDayTotals;
    }
    return userDayTotals;
  }, [userDayTotals, systemDayTotals]);

  // Create table rows
  const tableRows: RowItem[] = useMemo(() => {
    const rows: RowItem[] = [];
    groups.forEach(group => {
      rows.push({
        type: 'group',
        id: group.id,
        name: group.name,
        rate: group.rate,
        level: 0,
        isRealGroup: group.isRealGroup, // Pass through the flag
      });

      // Show users if group is expanded (real groups can have 0 users initially)
      if (expandedGroups.has(group.id) && group.isRealGroup) {
        group.users.forEach(user => {
          rows.push({
            type: 'user',
            id: user.id,
            name: user.name,
            rate: user.rate,
            level: 1,
            groupId: group.id,
          });
        });
      }
    });
    return rows;
  }, [groups, expandedGroups]);

  const calculations = useTipCalculations({ groups, dayTotals, dates, tableRows });

  // Build the complete distribution configuration object
  // This object contains all info needed to save and restore the table state
  const distributionConfig: TipDistributionConfig = useMemo(() => {
    // Build groups configuration
    const configGroups: TipDistributionGroup[] = groups.map(group => ({
      id: group.id,
      name: group.name,
      rate: group.rate,
      isRealGroup: group.isRealGroup ?? false,
      users: group.users.map(user => ({
        id: user.id,
        name: user.name,
        rate: user.rate,
        attendance: { ...user.attendance },
      })),
      attendance: { ...group.attendance },
    }));

    // Build days configuration
    const configDays: TipDistributionDay[] = dates.map(date => ({
      date,
      declaredTotal: dayTotals[date] || 0,
      systemTotal: systemDayTotals[date] || 0,
      distributedTotal: calculations.getDayTotal(date),
    }));

    // Calculate totals
    const totalDeclared = configDays.reduce((sum, day) => sum + day.declaredTotal, 0);
    const totalSystem = configDays.reduce((sum, day) => sum + day.systemTotal, 0);
    const totalDistributed = configDays.reduce((sum, day) => sum + day.distributedTotal, 0);

    // Get shift IDs from selected shifts
    const shiftIds = selectedShifts.map(shift => shift.shift_id);
    const sessionIds = [...new Set(selectedShifts.map(shift => shift.session_id))];

    return {
      status: 'draft',
      startDate: dates[0] || '',
      endDate: dates[dates.length - 1] || '',
      shiftIds,
      sessionIds,
      groups: configGroups,
      days: configDays,
      totals: {
        declared: totalDeclared,
        system: totalSystem,
        distributed: totalDistributed,
        difference: totalDeclared - totalSystem,
      },
    };
  }, [groups, dates, dayTotals, systemDayTotals, calculations, selectedShifts]);

  // Notify parent when config changes
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange(distributionConfig);
    }
  }, [distributionConfig, onConfigChange]);

  // Handlers
  const handleUpdateUserRate = (userId: string, newRate: number) => {
    setGroups(prev =>
      prev.map(g => ({
        ...g,
        users: g.users.map(u => (u.id === userId ? { ...u, rate: newRate } : u)),
      }))
    );
  };

  const handleUpdateGroupRate = (groupId: string, newRate: number) => {
    setGroups(prev =>
      prev.map(g => (g.id === groupId ? { ...g, rate: newRate } : g))
    );
  };

  const handleUpdateAttendance = (itemId: string, date: string, attended: boolean) => {
    setGroups(prev =>
      prev.map(g => {
        if (g.id === itemId) {
          return {
            ...g,
            attendance: { ...g.attendance, [date]: attended },
          };
        }
        return {
          ...g,
          users: g.users.map(u =>
            u.id === itemId
              ? { ...u, attendance: { ...u.attendance, [date]: attended } }
              : u
          ),
        };
      })
    );
  };

  const handleToggleGroupExpanded = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleStartAddUserToGroup = (groupId: string) => {
    const newUserId = `user-${Date.now()}`;
    const newUser: User = {
      id: newUserId,
      name: '',
      rate: 100,
      attendance: dates.reduce((acc, date) => ({ ...acc, [date]: true }), {}),
    };

    setGroups(prev =>
      prev.map(g =>
        g.id === groupId ? { ...g, users: [...g.users, newUser] } : g
      )
    );

    setEditingUserId(newUserId);
    setEditingUserName('');
    setExpandedGroups(prev => new Set([...prev, groupId]));
  };

  const handleConfirmUserName = (userId: string) => {
    if (!editingUserName.trim()) {
      handleCancelUserEdit(userId);
      return;
    }

    setGroups(prev =>
      prev.map(g => ({
        ...g,
        users: g.users.map(u =>
          u.id === userId ? { ...u, name: editingUserName.trim() } : u
        ),
      }))
    );

    setEditingUserId(null);
    setEditingUserName('');
  };

  const handleCancelUserEdit = (userId: string) => {
    setGroups(prev =>
      prev.map(g => ({
        ...g,
        users: g.users.filter(u => u.id !== userId),
      }))
    );
    setEditingUserId(null);
    setEditingUserName('');
  };

  const handleConfirmGroup = () => {
    if (!groupName.trim()) return;

    const newGroup: Group = {
      id: `group-${Date.now()}`,
      name: groupName.trim(),
      rate: 100,
      users: [],
      attendance: dates.reduce((acc, date) => ({ ...acc, [date]: true }), {}),
      isRealGroup: true, // This is a real group created by user
    };

    setGroups(prev => [...prev, newGroup]);
    setShowAddGroupModal(false);
    setGroupName('');
    setExpandedGroups(prev => new Set([...prev, newGroup.id]));
  };

  const handleUpdateDayTotal = (date: string, newValue: number) => {
    setUserDayTotals(prev => ({
      ...prev,
      [date]: newValue,
    }));
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleSaveRecord = async () => {
    if (isSaving) return;

    // If external handler provided, use it
    if (onSaveRecord) {
      await onSaveRecord();
      return;
    }

    try {
      setIsSavingLocal(true);

      // Get local_id from localStorage
      const userContext = localStorage.getItem('user');
      let localId = 1;
      if (userContext) {
        try {
          const parsed = JSON.parse(userContext);
          localId = parsed.local_id || 1;
        } catch (e) {
          console.error('Error parsing user context:', e);
        }
      }

      // Build the payload in the format expected by the API
      const daysPayload: any = {};
      
      dates.forEach(date => {
        // Get all users for this day (from all groups and standalone users)
        const usersForDay: any[] = [];
        
        groups.forEach(group => {
          if (group.isRealGroup && group.users.length > 0) {
            // Real group with users - save ALL users with their attendance for ALL days
            group.users.forEach(user => {
              usersForDay.push({
                user_id: parseInt(user.id.replace('user-', '')) || 0,
                user_name: user.name,
                rate: user.rate,
                group: group.name,
                attended: user.attendance, // Full attendance object for all days
              });
            });
          } else if (!group.isRealGroup) {
            // Standalone user (not a real group) - save with full attendance for all days
            usersForDay.push({
              user_id: parseInt(group.id.replace('user-', '')) || 0,
              user_name: group.name,
              rate: group.rate,
              attended: group.attendance, // Full attendance object for all days
            });
          }
        });

        daysPayload[date] = {
          value: dayTotals[date] || 0,
          users: usersForDay,
        };
      });

      const payload = {
        data: {
          start_date: distributionConfig.startDate,
          end_date: distributionConfig.endDate,
          total_tips: distributionConfig.totals.declared,
          total_distributed: distributionConfig.totals.distributed,
          total_employees: groups.reduce((sum, g) => {
            if (g.isRealGroup) return sum + g.users.length;
            return sum + 1; // Standalone user counts as 1
          }, 0),
          days: daysPayload,
        },
      };

      const response = await createTipRecord(localId, payload.data);
      
      if (response) {
        toast.success('Registro guardado exitosamente');
        onRecordSaved?.();
      } else {
        toast.error('Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error saving tip record:', error);
      toast.error('Error al guardar el registro');
    } finally {
      setIsSavingLocal(false);
    }
  };

  const handleDeleteUser = (userId: string, groupId?: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }

    if (groupId) {
      // User within a group
      setGroups(prev =>
        prev.map(g =>
          g.id === groupId
            ? { ...g, users: g.users.filter(u => u.id !== userId) }
            : g
        )
      );
    } else {
      // Standalone user (stored as a group with no users)
      setGroups(prev => prev.filter(g => g.id !== userId));
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const userCount = group.users.length;
    const message = userCount > 0
      ? `¿Estás seguro de que deseas eliminar este grupo y sus ${userCount} usuario(s)?`
      : '¿Estás seguro de que deseas eliminar este grupo?';

    if (!window.confirm(message)) {
      return;
    }

    setGroups(prev => prev.filter(g => g.id !== groupId));
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  };

  const handleAddUserToGroup = () => {
    if (!newUserName.trim()) return;

    if (!selectedGroupForUser) {
      // Add as standalone user (NOT a real group)
      const newUserId = `user-${Date.now()}`;
      const newStandaloneUser: Group = {
        id: newUserId,
        name: newUserName.trim(),
        rate: 100,
        users: [], // Empty array means it's a standalone user
        attendance: dates.reduce((acc, date) => ({ ...acc, [date]: true }), {}),
        isRealGroup: false, // This is a standalone user, NOT a group
      };

      setGroups(prev => [...prev, newStandaloneUser]);
    } else {
      // Add user to an existing group
      const newUserId = `user-${Date.now()}`;
      const newUser: User = {
        id: newUserId,
        name: newUserName.trim(),
        rate: 100,
        attendance: dates.reduce((acc, date) => ({ ...acc, [date]: true }), {}),
      };

      setGroups(prev =>
        prev.map(g =>
          g.id === selectedGroupForUser ? { ...g, users: [...g.users, newUser] } : g
        )
      );
      setExpandedGroups(prev => new Set([...prev, selectedGroupForUser]));
    }

    setShowAddUserModal(false);
    setSelectedGroupForUser(null);
    setNewUserName('');
  };

  return (
    <div className="tip-distribution-table">
      {!isViewMode && (
        <div className="table-actions">
          <button className="btn-action" onClick={() => setShowAddGroupModal(true)}>
            <span className="btn-icon">+</span>
            Agregar Grupo
          </button>
          <button className="btn-action btn-add-user" onClick={() => {
            setSelectedGroupForUser(null);
            setShowAddUserModal(true);
          }}>
            <span className="btn-icon">+</span>
            Agregar Usuario
          </button>
        </div>
      )}

      {showScrollButtons && (
        <div className="table-scroll-controls">
          <button 
            className="scroll-btn scroll-btn-left" 
            onClick={handleScrollLeft}
            title="Desplazar a la izquierda"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            className="scroll-btn scroll-btn-right" 
            onClick={handleScrollRight}
            title="Desplazar a la derecha"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      <div className="tip-management-table-wrapper">
        <div className="tip-management-table" ref={scrollContainerRef}>
          <TipTableHeader dates={dates} />

          {tableRows.map(row => (
            <TipTableRow
              key={row.id}
              row={row}
              dates={dates}
              groups={groups}
              expandedGroups={expandedGroups}
              editingUserId={editingUserId}
              editingUserName={editingUserName}
              onToggleGroupExpanded={handleToggleGroupExpanded}
              onStartAddUserToGroup={handleStartAddUserToGroup}
              onConfirmUserName={handleConfirmUserName}
              onCancelUserEdit={handleCancelUserEdit}
              onEditingUserNameChange={setEditingUserName}
              onUpdateUserRate={handleUpdateUserRate}
              onUpdateGroupRate={handleUpdateGroupRate}
              onUpdateAttendance={handleUpdateAttendance}
              calculateUserTip={calculations.calculateUserTip}
              getUserTotalTips={calculations.getUserTotalTips}
              getGroupTipForDay={calculations.getGroupTipForDay}
              getGroupTotalTips={calculations.getGroupTotalTips}
              isViewMode={isViewMode}
              onDeleteUser={handleDeleteUser}
              onDeleteGroup={handleDeleteGroup}
            />
          ))}

          <TipTotalsRow
            dates={dates}
            dayTotals={dayTotals}
            originalDayTotals={systemDayTotals}
            onUpdateDayTotal={handleUpdateDayTotal}
            getDayTotal={calculations.getDayTotal}
            isViewMode={isViewMode}
          />
        </div>
      </div>

      <AddGroupModal
        isOpen={showAddGroupModal}
        groupName={groupName}
        onGroupNameChange={setGroupName}
        onConfirm={handleConfirmGroup}
        onClose={() => {
          setShowAddGroupModal(false);
          setGroupName('');
        }}
      />

      {showAddUserModal && (
        <AddUserModal
          isOpen={showAddUserModal}
          userName={newUserName}
          selectedGroupId={selectedGroupForUser || ''}
          groups={groups}
          onUserNameChange={setNewUserName}
          onGroupChange={setSelectedGroupForUser}
          onConfirm={handleAddUserToGroup}
          onClose={() => {
            setShowAddUserModal(false);
            setSelectedGroupForUser(null);
            setNewUserName('');
          }}
        />
      )}
    </div>
  );
};

export default TipDistributionTable;
